import { WORK_ORDER_SEVERITIES, WORK_ORDER_SOURCES } from '@chukua/common';
import { Alert, Button, Drawer, Form, Input, Select } from 'antd';
import { TruckSearchSelect } from '../../../../fleet/features/trucks/public/TruckSearchSelect';
import { blankRaiseForm, type RaiseWorkOrderFormValues } from '../policies/raise-work-order.policy';
import { WORK_ORDER_SEVERITY_LABELS, WORK_ORDER_SOURCE_LABELS } from '../policies/work-order-labels';
import { EstimateField } from './EstimateField';

const SOURCE_OPTIONS = WORK_ORDER_SOURCES.map((source) => ({ value: source, label: WORK_ORDER_SOURCE_LABELS[source] }));
const SEVERITY_OPTIONS = WORK_ORDER_SEVERITIES.map((severity) => ({ value: severity, label: WORK_ORDER_SEVERITY_LABELS[severity] }));

interface RaiseWorkOrderDrawerProps {
  open: boolean;
  error: string | null;
  submitting: boolean;
  onSubmit: (values: RaiseWorkOrderFormValues) => Promise<boolean>;
  onClose: () => void;
}

/** Raising names the truck, what prompted it, how urgent it is and an estimate: the estimate is what decides who has to approve. The workshop is chosen later, on the order. */
export function RaiseWorkOrderDrawer({ open, error, submitting, onSubmit, onClose }: RaiseWorkOrderDrawerProps) {
  const [form] = Form.useForm<RaiseWorkOrderFormValues>();

  async function submit(values: RaiseWorkOrderFormValues) {
    if (!(await onSubmit({ ...blankRaiseForm(), ...values }))) return;
    form.resetFields();
    onClose();
  }

  return (
    <Drawer open={open} onClose={onClose} title="Raise work order" size={480} destroyOnHidden>
      {error && <Alert className="mb-5" type="error" title={error} showIcon />}
      <Form<RaiseWorkOrderFormValues>
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={submit}
        disabled={submitting}
        initialValues={blankRaiseForm()}
      >
        <Form.Item name="truckId" label="Truck" rules={[{ required: true, message: 'Choose the truck' }]}>
          <TruckSearchSelect />
        </Form.Item>
        <Form.Item name="source" label="What prompted it" rules={[{ required: true, message: 'Choose what prompted it' }]}>
          <Select options={SOURCE_OPTIONS} placeholder="Select a source" />
        </Form.Item>
        <Form.Item name="severity" label="Severity" rules={[{ required: true, message: 'Choose the severity' }]}>
          <Select options={SEVERITY_OPTIONS} placeholder="Select a severity" />
        </Form.Item>
        <Form.Item name="title" label="Title" rules={[{ required: true, whitespace: true, message: 'Enter a title' }, { max: 200 }]}>
          <Input placeholder="Replace front brake pads" />
        </Form.Item>
        <Form.Item name="description" label="Details" rules={[{ max: 2000 }]}>
          <Input.TextArea rows={3} />
        </Form.Item>
        <Form.Item name="sourceRef" label="Reference" extra="The defect, incident or inspection number, if there is one." rules={[{ max: 100 }]}>
          <Input />
        </Form.Item>
        <EstimateField />
        <Form.Item className="mb-0 pt-2">
          <Button type="primary" htmlType="submit" loading={submitting}>
            Raise work order
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
}
